/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from "react";
import { useSelector } from "react-redux";

import {
  BASIC,
  BRAND_AVANTI,
  BRAND_STAYSURE,
  CLASSIC,
  ESSENTIALS,
} from "@/constants";
import { useBrand } from "@/providers/brand-provider";
import { RootState } from "@/store/root-reducer";
import { PolicyPackagePrice } from "@/types/PolicyPackagePriceType";

interface ExtendedWindow extends Window {
  dataLayer?: any[];
  optimizely?: any[];
}

/**
 * Custom hook for tracking PTM Exit Modal events with GTM and Optimizely
 * @returns Object with tracking methods
 */
export const useEventTracking = () => {
  const { brandName } = useBrand();
  const { attributes, referenceId } = useSelector(
    (state: RootState) => state.quote
  );
  const policyPackagePrice: PolicyPackagePrice[] =
    attributes.priceResult?.policyPackagePrice || [];
  const isCruise = attributes.tripDetails?.goingOnCruise;
  const modalName = `${brandName}_ecosystem_exit_intent`;

  const getQuotePrice = () => {
    if (brandName === BRAND_STAYSURE)
      return policyPackagePrice.find(
        (value: PolicyPackagePrice) => value.policyPackage === BASIC
      )?.total;
    else if (brandName === BRAND_AVANTI && isCruise === true)
      return policyPackagePrice.find(
        (value: PolicyPackagePrice) => value.policyPackage === CLASSIC
      )?.total;
    else if (brandName === BRAND_AVANTI && isCruise === false)
      return policyPackagePrice.find(
        (value: PolicyPackagePrice) => value.policyPackage === ESSENTIALS
      )?.total;
  };

  /**
   * Trigger Optimizely Events
   * @param eventKey - The event key to trigger in Optimizely
   */
  const triggerOptimizelyEvent = useCallback((eventKey: string): void => {
    try {
      if (typeof window === "undefined") return;
      // Initialise the queue if the Optimizely script hasn't loaded yet.
      // Optimizely replays queued commands on init — the intended SPA pattern.
      (window as any).optimizely = (window as any).optimizely || [];
      (window as any).optimizely.push({
        type: "event",
        eventName: eventKey,
      });
    } catch (error) {
      console.log("Error :- Optimizely is not initialize properly", error);
    }
  }, []);

  /**
   * Trigger GTM Popup Events for PTM Exit Modal
   * @param eventName - The GTM event name
   * @param additionalData - Additional data to include in the GTM event
   */
  const triggerGTMEvent = useCallback(
    (eventName: string, additionalData?: Record<string, any>): void => {
      try {
        if (typeof window === "undefined") return;
        const extendedWindow = window as ExtendedWindow;
        extendedWindow.dataLayer = extendedWindow.dataLayer || [];
        extendedWindow.dataLayer.push({
          event: "content_pop_up",
          event_name: eventName,
          popup_name: modalName,
          eco_touchpoint: "quote_price_exit_intent",
          quote_id: referenceId,
          quote_price: getQuotePrice(),
          currency: "GBP",
          ...additionalData,
        });
      } catch (error) {
        console.log("Error :- GTM is not initialize properly", error);
      }
    },
    [getQuotePrice, modalName, referenceId, policyPackagePrice, isCruise]
  );

  return {
    triggerOptimizelyEvent,
    triggerGTMEvent,
  };
};
